<template>
    <div class="lx-grid" :style="{ height: `calc(100% - ${offsetTopComputed}px)` }">
        <b-button-toolbar class="lx-grid-buttons ml-1" v-if="isSearch || slotPassed">
            <b-button-toolbar class="lx-grid-buttons-toolbar align-items-start" :class="buttonToolbarClass">
                <slot></slot>
            </b-button-toolbar>
            <grid-search @search="onSearch" class="m-1" v-if="isSearch"></grid-search>
        </b-button-toolbar>
        <div ref="gridContainer" :style="{ height: gridHeight }">
            <ag-grid-vue
                :animateRows="false"
                :autoGroupColumnDef="autoGroupColumnDef"
                ref="grid"
                :cacheBlockSize="cacheBlockSize"
                :cacheOverflowSize="2"
                :class="agGridTheme"
                :columnDefs="columnDefs"
                :columnTypes="columnTypes"
                :defaultColDef="computedDefaultColDef"
                :detailCellRenderer="detailCellRenderer"
                :detailCellRendererParams="detailCellRendererParams"
                :detailRowAutoHeight="true"
                :doesExternalFilterPass="doesExternalFilterPass"
                :domLayout="domLayout"
                :enableCellTextSelection="enableCellTextSelection"
                :getDataPath="getDataPath"
                :getRowClass="getRowClass"
                :getRowId="getRowId"
                :getRowHeight="getRowHeight"
                :getRowStyle="getRowStyle"
                :getServerSideGroupKey="getServerSideGroupKey"
                :gridOptions="gridOptions"
                :groupDisplayType="groupDisplayType"
                :groupRowRendererParams="groupRowRendererParams"
                :infiniteInitialRowCount="1"
                :isExternalFilterPresent="isExternalFilterPresent"
                :isRowSelectable="isRowSelectable"
                :isServerSideGroup="isServerSideGroup"
                :isRowMaster="isRowMaster"
                :overlayLoadingTemplate="overlayLoadingTemplate"
                :overlayNoRowsTemplate="overlayNoRowsTemplate"
                :masterDetail="masterDetail"
                :maxBlocksInCache="maxBlocksInCache"
                :maxConcurrentDatasourceRequests="2"
                :pagination="pagination"
                :paginationPageSize="paginationPageSize"
                :paginationPageSizeSelector="false"
                :loading="loading"
                :pinnedBottomRowData="pinnedBottomRowData"
                :pinnedTopRowData="pinnedTopRowData"
                :resetRowDataOnUpdate="true"
                :rowBuffer="rowBuffer"
                :rowData="rowData || null"
                :rowModelType="rowModelType"
                :rowSelection="rowSelection"
                :singleClickEdit="true"
                :statusBar="statusBar"
                :suppressContextMenu="true"
                :suppressColumnVirtualisation="suppressColumnVirtualisation"
                :suppressRowHoverHighlight="suppressRowHoverHighlight"
                :suppressRowClickSelection="suppressRowClickSelection"
                :suppressRowDeselection="suppressRowDeselection"
                :suppressScrollOnNewData="suppressScrollOnNewData"
                :treeData="treeData"
                style="width: 100%; height: 100%"
                :is-group-open-by-default="isGroupOpenByDefault"
                :includeHiddenColumnsInQuickFilter="!!treeData"
                @grid-ready="onGridReady"
                @row-data-updated="onRowDataUpdated"
                @filter-changed="onFilterChanged"
                @row-drag-end="onRowDragEnd"
                @selection-changed="onSelectionChanged"
                @gridSizeChanged="onGridSizeChanged"
                @modelUpdated="onModelUpdated"
                @cell-editing-started="onCellEditingStarted"
                @cell-editing-stopped="onCellEditingStopped"
                @firstDataRendered="onFirstDataRendered"
                @columnResized="onColumnResized"
                @sortChanged="onSortChanged"
                @rowGroupOpened="onRowGroupOpened"
            ></ag-grid-vue>
        </div>
    </div>
</template>

<script>
    import { AgGridVue } from 'ag-grid-vue3';
    import gridSearch from '../grid/grid-search.vue';
    import { mapGetters } from 'vuex';
    import isNil from 'lodash/isNil';
    import mapSort from 'mapsort';
    import format from 'date-fns/format';
    import parseISO from 'date-fns/parseISO';
    import BadgeCellRenderer from './badge-cell-renderer.vue';
    import DateTimeCellRenderer from './date-time-cell-renderer.vue';
    import InputCellEditor from './input-cell-editor.vue';
    import RadioCellRenderer from './radio-cell-renderer.vue';
    import SelectCellEditor from './select-cell-editor.vue';
    import MultiSelectCellEditor from './multi-select-cell-editor.vue';
    import SwitchCellRenderer from './switch-cell-renderer.vue';
    import YesNoCellRenderer from './yes-no-cell-renderer.vue';
    import YesNoCellRendererNew from './yes-no-cell-renderer-new.vue';
    import AttachmentCellRenderer from './attachment-cell-renderer.vue';
    import TransactionPaymentMethodCellRenderer from './transaction-payment-method-cell-renderer.vue';
    import LinkCellRenderer from './link-renderer.vue';
    import DatepickerCellEditor from './datepicker-cell-editor.vue';
    import { renderGuestStatusIcon } from '../../dashboard/widgets/helpers';
    import { toAmountDisplay } from '../../../utils/numbers';

    export default {
        name: 'Grid',
        components: {
            AgGridVue,
            gridSearch,
            badgeCellRenderer: BadgeCellRenderer,
            yesNoCellRenderer: YesNoCellRenderer,
            dateTimeCellRenderer: DateTimeCellRenderer,
            inputCellEditor: InputCellEditor,
            radioCellRenderer: RadioCellRenderer,
            selectCellEditor: SelectCellEditor,
            multiSelectCellEditor: MultiSelectCellEditor,
            switchCellRenderer: SwitchCellRenderer,
            yesNoCellRendererNew: YesNoCellRendererNew,
            attachmentCellRenderer: AttachmentCellRenderer,
            transactionPaymentMethodCellRenderer: TransactionPaymentMethodCellRenderer,
            linkCellRenderer: LinkCellRenderer,
            datepickerCellEditor: DatepickerCellEditor,
        },
        props: {
            autoGroupColumnDef: {
                type: Object,
                default: null,
            },
            autoWidthExclude: {
                type: Array,
                default: null,
            },
            autoWidthSkipHeader: {
                type: Boolean,
                default: true,
            },
            buttonToolbarClass: {
                type: Array,
                default: () => ['m-1'],
            },
            cacheBlockSize: {
                type: Number,
                default: 30,
            },
            canDragRow: {
                type: Function,
                default: () => true,
            },
            canDropRow: {
                type: Function,
                default: () => true,
            },
            columnDefs: Array,
            customGridHeight: {
                type: String,
                default: null,
            },
            customDomLayout: {
                type: String,
                default: null,
            },
            defaultColDef: {
                type: Object,
                default: () => {},
            },
            defaultSort: {
                type: String,
                default: null,
            },
            defaultDesc: {
                type: Boolean,
                default: false,
            },
            detailCellRenderer: {
                type: String,
                default: '',
            },
            detailCellRendererParams: {
                type: [Object, Function],
                default: undefined,
            },
            doesExternalFilterPass: {
                type: Function,
                default: () => true,
            },
            editAutoWidthColumns: {
                type: Array,
                default: () => [],
            },
            enableCellTextSelection: {
                type: Boolean,
                default: true,
            },
            fontSize: {
                type: Number,
                default: null,
            },
            fetchCallback: {
                type: Function,
                default: () => {},
            },
            getDataPath: {
                type: Function,
                default: null,
            },
            getRowClass: {
                type: Function,
                default: null,
            },
            getRowId: {
                type: Function,
                default: (item) => item.data.id?.toString(),
            },
            getRowHeight: {
                type: Function,
                default: null,
            },
            getRowStyle: {
                type: Function,
                default: null,
            },
            getServerSideGroupKey: {
                type: Function,
                default: () => 0,
            },
            gridTheme: {
                type: String,
                default: 'ag-theme-alpine',
            },
            groupDisplayType: {
                type: String,
                default: 'singleColumn',
            },
            groupDefaultExpanded: {
                type: Number,
                default: 0,
            },
            groupRowRendererParams: {
                type: Object,
                default: () => {},
            },
            isAutoHeight: {
                type: Boolean,
                default: false,
            },
            isAutoWidth: {
                type: Boolean,
                default: false,
            },
            isExternalFilterPresent: {
                type: Function,
                default: () => false,
            },
            isRowSelectable: {
                type: Function,
                default: () => true,
            },
            isSearch: {
                type: Boolean,
                default: true,
            },
            isServerSideGroup: {
                type: Function,
                default: () => false,
            },
            isRowMaster: {
                type: Function,
                default: undefined,
            },
            headerHeight: {
                type: Number,
                default: 35,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            masterDetail: {
                type: Boolean,
                default: false,
            },
            maxBlocksInCache: {
                type: Number,
                default: 50,
            },

            offsetTop: {
                type: Number,
                default: null,
            },
            onCellClicked: {
                type: Function,
                default: () => {},
            },
            overlayLoadingTemplate: {
                type: String,
                default: '<span class="ag-overlay-loading-center">Loading...</span>'
            },
            overlayNoRowsTemplate: {
                type: String,
                default: 'Nothing found',
            },
            pagination: {
                type: Boolean,
                default: false,
            },
            paginationPageSize: {
                type: Number,
                default: null,
            },
            pinnedTopRowData: {
                type: Array,
                default: null,
            },
            pinnedBottomRowData: {
                type: Array,
                default: null,
            },
            rowBuffer: {
                type: Number,
                default: 10,
            },
            rowData: {
                type: Array,
                default: undefined,
            },
            rowSelection: {
                type: String,
                default: null,
            },
            saveRowOrder: {
                type: Function,
                default: () => {},
            },
            statusBar: {
                type: Object,
                default: null,
            },
            suppressColumnVirtualisation: {
                type: Boolean,
                default: false,
            },
            suppressScrollOnNewData: {
                type: Boolean,
                default: false,
            },
            suppressRowClickSelection: {
                type: Boolean,
                default: false,
            },
            suppressRowDeselection: {
                type: Boolean,
                default: false,
            },
            suppressRowHoverHighlight: {
                type: Boolean,
                default: false,
            },
            treeData: {
                type: Boolean,
                default: false,
            },
            isGroupOpenByDefault: {
                type: Function,
                default: () => false,
            },
        },
        data: function () {
            return {
                el: null,
                gridContainerEl: null,
                widthAdjusted: 0,
                search: '',
                gridApi: {},
                gridOptions: {
                    multiSortKey: 'ctrl',
                    suppressCellFocus: true,
                    stopEditingWhenCellsLoseFocus: true,
                    headerHeight: this.headerHeight,
                    onCellClicked: this.onCellClicked,
                    groupDefaultExpanded: this.groupDefaultExpanded,
                    enableCellTextSelection: this.enableCellTextSelection,
                },
                modules: [],
                myDefaultColDef: {
                    flex: 1,
                    minWidth: 100,
                    resizable: true,
                    sortable: true,
                    suppressHeaderMenuButton: true,
                    suppressMovable: true,
                },
                maxOrderForGroup: {
                    ROOT_NODE_ID: 0,
                },
                totalRecords: -1,
            };
        },
        computed: {
            ...mapGetters('header', ['userCurrencySymbol', 'userUnicodeDateFormat', 'userTimeFormat']),
            agGridTheme() {
                return `ag-theme-alpine ${this.gridTheme} ${this.fontSize ? 'ag-font-size-' + this.fontSize : ''}`;
            },
            columnTypes() {
                return {
                    idColumn: {
                        headerName: 'ID',
                        sort: 'desc',
                    },
                    orderColumn: {
                        field: 'order',
                        headerName: '#',
                        minWidth: 90,
                        sort: 'asc',
                        sortable: false,
                        editable: true,
                        cellEditor: 'inputCellEditor',
                        cellEditorParams: { isNumeric: true, select: true },
                        cellClass: 'text-primary lx-editable-cell',
                        rowDrag: true,
                        valueSetter: ({ oldValue, newValue, node, api }) => {
                            let newOrder = parseInt(newValue, 10);
                            if (!isNaN(newOrder) && newOrder > 0) {
                                this.moveRow({
                                    oldOrder: oldValue,
                                    newOrder,
                                    node,
                                    api,
                                });
                            }
                            return false;
                        },
                    },
                    amountColumn: {
                        pdfExportOptions: {
                            valueFormatter: ({ value, noNegative, percentage = false }) => {
                                if (!isNil(value) && !isNaN(value)) {
                                    return toAmountDisplay({
                                        amount: value,
                                        currency: this.userCurrencySymbol,
                                        noNegative,
                                        percentage,
                                    });
                                }
                            },
                        },
                        cellRenderer: ({ value, noNegative, percentage = false }) => {
                            if (!isNil(value) && !isNaN(value)) {
                                return toAmountDisplay({
                                    amount: value,
                                    currency: this.userCurrencySymbol,
                                    noNegative,
                                    percentage,
                                });
                            }
                        },
                        cellClass: 'ag-right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                        minWidth: 90,
                        sortable: true,
                    },
                    dateColumn: {
                        cellRenderer: ({ value, noValue }) => {
                            return value ? format(parseISO(value), this.userUnicodeDateFormat) : noValue || '';
                        },
                        pdfExportOptions: {
                            valueFormatter: ({ value }) =>
                                value ? format(parseISO(value), this.userUnicodeDateFormat) : '',
                        },
                    },
                    badgeColumn: {
                        cellRenderer: 'badgeCellRenderer',
                    },
                    dateTimeColumn: {
                        cellRenderer: 'dateTimeCellRenderer',
                        pdfExportOptions: {
                            valueFormatter: ({ value }) =>
                                value
                                    ? format(parseISO(value), `${this.userUnicodeDateFormat} ${this.userTimeFormat}`)
                                    : '',
                        },
                    },
                    editableColumn: {
                        editable: true,
                        cellEditor: 'inputCellEditor',
                    },
                    guestStatusColumn: {
                        cellRenderer: ({ value }) => (value ? renderGuestStatusIcon(value) : ''),
                        cellClass: 'guest-status-cell',
                        maxWidth: 70,
                        minWidth: 70,
                    },
                    radioColumn: {
                        cellRenderer: 'radioCellRenderer',
                    },
                    dateEditableColumn: {
                        editable: true,
                        cellEditor: 'datepickerCellEditor',
                    },
                    switchColumn: {
                        cellRenderer: 'switchCellRenderer',
                        cellClass: 'lx-switch-cell',
                    },
                    yesNoColumn: {
                        cellRenderer: 'yesNoCellRenderer',
                        pdfExportOptions: {
                            valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
                        },
                    },
                    yesNoColumnNew: {
                        cellRenderer: 'yesNoCellRendererNew',
                    },
                    attachmentColumn: {
                        cellRenderer: 'attachmentCellRenderer',
                    },
                    transactionPaymentMethodColumn: {
                        cellRenderer: 'transactionPaymentMethodCellRenderer',
                    },
                    linkColumn: {
                        cellRenderer: 'linkCellRenderer',
                    },
                };
            },
            computedDefaultColDef() {
                return Object.assign({}, this.myDefaultColDef, this.defaultColDef);
            },
            datasource() {
                return {
                    getRows: async (params) => {
                        const request = params.request;
                        const offset = request.startRow;
                        const limit = request.endRow - offset;
                        const payload = {
                            group: null,
                            offset: offset,
                            limit: limit,
                            sort: this.defaultSort || 'id',
                            desc: this.defaultDesc,
                            search: this.search,
                        };
                        if (request.sortModel.length) {
                            const sortModel = request.sortModel[0];
                            payload.sort = sortModel.colId;
                            payload.desc = sortModel.sort === 'desc';
                        }
                        if (request.groupKeys.length) {
                            payload.group = request.groupKeys[0];
                        }
                        try {
                            const response = await this.fetchCallback(payload);
                            const data = response.data.data || response.data || [];

                            this.totalRecords = response.data.total_records || 0;
                            this.$emit('on-total-records', this.totalRecords);

                            let lastRow = -1;
                            if (data.length < limit) {
                                lastRow = offset + data.length;
                            } else if (this.totalRecords) {
                                lastRow = this.totalRecords;
                            }
                            params.success({
                                rowData: data,
                                rowCount: lastRow,
                            });
                        } catch (error) {
                            this.errorToast({ text: 'Could not load data.' });
                            params.fail();
                        }
                        if (this.gridOptions.api.getDisplayedRowCount() < 1) {
                            this.gridOptions.api.showNoRowsOverlay();
                        } else {
                            this.gridOptions.api.hideOverlay();
                        }
                        if (this.rowModelType == 'serverSide') {
                            this.widthAdjusted = 0;
                        }
                    },
                };
            },
            domLayout() {
                return this.customDomLayout || (this.isAutoHeight ? 'autoHeight' : 'normal');
            },

            gridOffsetTop() {
                return this.gridContainerEl ? this.gridContainerEl.offsetTop : 0;
            },
            gridHeight() {
                if (this.customGridHeight) return `${this.customGridHeight}px`
                return this.isAutoHeight ? '' : `calc(100% - ${this.gridOffsetTop}px)`;
            },
            hasOrderColumn() {
                return this.columnDefs.findIndex((item) => item.type === 'orderColumn') >= 0;
            },
            offsetTopComputed() {
                return !isNil(this.offsetTop) ? this.offsetTop : this.el ? this.el.offsetTop : 0;
            },
            rowModelType() {
                return this.rowData !== undefined ? 'clientSide' : 'serverSide';
            },
            slotPassed() {
                return this.$slots.default && this.$slots.default()[0];
            },
        },
        mounted() {
            this.el = this.$el;
            this.gridContainerEl = this.$refs.gridContainer;
            this.gridOptions.api = this.$refs.grid.api;
            this.gridOptions.api.hideOverlay();

            if (this.rowModelType === 'clientSide') {
                window.addEventListener('beforeprint', (event) => {
                    this.gridOptions.api.setDomLayout('print');
                });
                window.addEventListener('afterprint', (event) => {
                    this.gridOptions.api.setDomLayout(this.domLayout);
                });
            }
        },
        methods: {
            autoSizeGrid({ api: columnApi }) {
                if (
                    this.isAutoWidth &&
                    this.widthAdjusted < 1 &&
                    (this.rowData === undefined || (!!this.rowData && this.rowData.length > 0))
                ) {
                    this.widthAdjusted = this.widthAdjusted + 1;
                    let allColumnIds = [];
                    let autoWidthExclude = this.autoWidthExclude;
                    if (autoWidthExclude === null) {
                        autoWidthExclude = [];
                    }
                    columnApi.getColumns().forEach(function (column) {
                        if (!autoWidthExclude.includes(column.colId) && !column.colDef.autoWidthExclude) {
                            allColumnIds.push(column.colId);
                        }
                    });
                    setTimeout(() => {
                        columnApi.autoSizeColumns(allColumnIds, this.autoWidthSkipHeader);
                    }, 100);
                }
            },
            getTotalRecords() {
                return this.totalRecords;
            },
            getMaxOrder(node) {
                return this.maxOrderForGroup[node.parent.id] || 0;
            },
            moveRow({ oldOrder, newOrder, node, api }) {
                const maxOrder = this.getMaxOrder(node);
                if (maxOrder) {
                    newOrder = Math.min(newOrder, maxOrder);
                }
                this.saveRowOrder({
                    row: node.data,
                    order: newOrder,
                    onSuccess: () => {
                        // Update grid
                        if (this.rowModelType == 'serverSide') {
                            api.refreshServerSide();
                        } else {
                            api.clearFocusedCell();
                            const update = [];
                            api.forEachNode((rowNode, index) => {
                                const row = rowNode.data;
                                if (this.canDragRow(row)) {
                                    if (oldOrder < newOrder) {
                                        if (row.order > oldOrder && row.order <= newOrder) {
                                            row.order--;
                                        }
                                    } else {
                                        if (row.order >= newOrder && row.order < oldOrder) {
                                            row.order++;
                                        }
                                    }
                                    update.push(row);
                                }
                            });
                            node.data.order = newOrder;
                            update.push(node.data);
                            api.applyTransaction({ update });
                        }
                    },
                    onError: (error) => {
                        this.errorToast({ text: 'Could not move row.' });
                    },
                });
            },
            onCellEditingStarted({ api: columnApi, column }) {
                if (this.editAutoWidthColumns.includes(column.colId)) {
                    columnApi.autoSizeColumns([column.colId], true);
                }
                this.$emit('cell-editing-started', { columnApi, column });
            },
            onCellEditingStopped({ api: columnApi, column }) {
                if (this.editAutoWidthColumns.includes(column.colId)) {
                    columnApi.autoSizeColumns([column.colId], true);
                }
                this.$emit('cell-editing-stopped', { columnApi, column });
            },
            onColumnResized() {
                this.$emit('column-resized');
            },
            onGridReady({ api }) {
                if (this.rowModelType == 'serverSide') {
                    api.setGridOption('serverSideDatasource', this.datasource);
                }
                api.setGridOption('applyQuickFilterBeforePivotOrAgg', false);
                this.$emit('on-grid-ready', { api });
            },
            onGridSizeChanged({ api }) {
                this.autoSizeGrid({ api });
            },
            onFilterChanged({ api }) {
                if (!api.getDisplayedRowCount()) {
                    api.showNoRowsOverlay();
                } else {
                    api.hideOverlay();
                }
            },
            onFirstDataRendered() {
                this.$emit('first-data-rendered');
            },
            onModelUpdated({ api }) {
                this.autoSizeGrid({ api });
            },
            onRowDataUpdated({ api }) {
                this.widthAdjusted = 0;
                this.setOrder();
                this.$emit('on-row-data-updated', { api });
                this.autoSizeGrid({ api });
                if (api.getDisplayedRowCount() < 1) api.showNoRowsOverlay();
                else api.hideOverlay();
            },
            onRowDragEnd({ node, overNode, api }) {
                if (!overNode || !overNode.data.order) {
                    // Dropped outside of grid
                    return;
                }
                const oldOrder = node.data.order;
                if (!oldOrder) {
                    // There is no "order" column
                    return;
                }
                const newOrder = overNode.data.order;
                if (
                    oldOrder !== newOrder &&
                    this.canDropRow({
                        row: node.data,
                        overRow: overNode.data,
                    })
                ) {
                    this.moveRow({ oldOrder, newOrder, node, api });
                }
            },
            onSearch(query) {
                this.search = query;
                if (this.rowModelType == 'serverSide') {
                    this.gridOptions.api.setGridOption('serverSideDatasource', this.datasource);
                } else {
                    this.gridOptions.api.setGridOption('quickFilterText', query);
                }
            },
            onSelectionChanged() {
                this.$emit('selection-changed');
            },
            onSortChanged(e) {
                this.$emit('sort-changed', e);
            },
            onRowGroupOpened(e) {
                this.$emit('row-group-opened', e);
            },
            refreshServerSide() {
                this.gridOptions.api.refreshServerSide({ purge: true });
            },
            refreshDataSource() {
                this.gridOptions.api.setGridOption('serverSideDatasource', this.datasource);
            },
            setOrder() {
                if (this.rowModelType === 'clientSide' && this.hasOrderColumn) {
                    this.setOrderForGroup(this.gridOptions.api.getModel().rootNode);
                }
            },
            setOrderForGroup(groupNode) {
                const sortedNodes = mapSort(
                    groupNode.childrenAfterGroup,
                    (element) => element.data.order,
                    (a, b) => (a < b ? -1 : 1)
                );
                const len = sortedNodes.length;
                let count = 0;
                for (let i = 0; i < len; i++) {
                    const node = sortedNodes[i];
                    if (node.allChildrenCount) {
                        this.setOrderForGroup(node);
                    }
                    const row = node.data;
                    if (row && this.canDragRow(row)) {
                        count++;
                        if (row.order !== count) {
                            row.order = count;
                            node.setData(row);
                        }
                    }
                }
                this.maxOrderForGroup[groupNode.id] = count;
            },
        },
        watch: {
            userUnicodeDateFormat() {
                this.gridOptions.api.redrawRows();
            },
        },
        //watch: {
        //    rowData() {
        //        console.log(this.rowData);
        //    },
        //},        
    };
</script>
<style lang="scss">
    @import '~ag-grid-community/styles/ag-grid.css';
    @import '~ag-grid-community/styles/ag-theme-alpine.css';

    .ag-theme-alpine {
        // @include ag-theme-alpine($ag-theme-alpine-default-params);

        .ag-layout-auto-height .ag-center-cols-clipper,
        .ag-layout-auto-height .ag-center-cols-container,
        .ag-layout-print .ag-center-cols-clipper,
        .ag-layout-print .ag-center-cols-container {
            min-height: 50px;
        }
        .ag-cell-wrapper.ag-row-group {
            align-items: center !important;
        }
    }

    .ag-theme-alpine-compact {
        // @extend .ag-theme-alpine;

        --ag-grid-size: 5px;
        --ag-border: none !important;
        --ag-odd-row-background-color: none;

        // @include ag-theme-alpine($compact-params);

        .ag-header {
            background-color: #fff !important;
            border-width: 1px !important;
            border-color: #dde2eb !important;
            border-color: var(--ag-row-border-color, var(--ag-secondary-border-color, #dde2eb)) !important;
            border-bottom-style: solid;
        }

        .ag-row-pinned {
            background-color: #fff !important;
            border-width: 1px !important;
            border-color: #dde2eb !important;
            border-color: var(--ag-row-border-color, var(--ag-secondary-border-color, #dde2eb)) !important;
            border-top-style: solid;
        }

        .ag-root-wrapper {
            border-radius: 5px;
            padding: 10px;
        }

        .ag-layout-auto-height .ag-center-cols-clipper,
        .ag-layout-auto-height .ag-center-cols-container,
        .ag-layout-print .ag-center-cols-clipper,
        .ag-layout-print .ag-center-cols-container,
        .ag-layout-auto-height .ag-center-cols-viewport {
            min-height: 0 !important;
        }

        .ag-header-cell-text {
            color: #3d3d3d;
            font-size: 16px !important;
        }

        .ag-cell-value {
            font-size: 16px !important;
        }

        .ag-status-bar {
            font-size: 16px !important;
        }

        .ag-row {
            border-bottom: none !important;
        }

        .ag-floting-bottom {
            border: none !important;
        }
    }

    .ag-theme-alpine-report {
        // $report-params: map-merge($ag-theme-alpine-default-params, (
        //     grid-size: 4px,
        // ));

        // @extend .ag-theme-alpine;

        --ag-grid-size: 4px;

        .ag-header {
            background-color: #fff !important;
        }

        .ag-root-wrapper {
            border-top: 0;
            overflow: visible;
        }

        .ag-layout-auto-height .ag-center-cols-clipper,
        .ag-layout-auto-height .ag-center-cols-container,
        .ag-layout-print .ag-center-cols-clipper,
        .ag-layout-print .ag-center-cols-container,
        .ag-layout-auto-height .ag-center-cols-viewport {
            min-height: 0;
        }

        .ag-header-cell-text {
            font-size: 12px !important;
        }

        .ag-cell-value {
            font-size: 12px !important;
        }

        .ag-status-bar {
            font-size: 12px !important;
        }

        .ag-right-aligned-header {
            text-align: right;
        }
    }

    .ag-centered-header {
        .ag-header-cell-label {
            display: flex;
            justify-content: center;
            text-align: center;
        }
    }

    .ag-theme-alpine-dashboard {
        @extend .ag-theme-alpine-report;
    }

    .ag-theme-alpine-partner {
        @extend .ag-theme-alpine-report;
    }

    .ag-font-size-10 {
        .ag-header-cell-text {
            font-size: 10px !important;
        }

        .ag-cell-value {
            font-size: 10px !important;
        }

        .ag-status-bar {
            font-size: 10px !important;
        }
    }

    .ag-font-size-12 {
        .ag-header-cell-text {
            font-size: 12px !important;
        }

        .ag-cell-value {
            font-size: 12px !important;
        }

        .ag-status-bar {
            font-size: 12px !important;
        }
    }

    .ag-font-size-14 {
        .ag-header-cell-text {
            font-size: 14px !important;
        }

        .ag-cell-value {
            font-size: 14px !important;
        }

        .ag-status-bar {
            font-size: 14px !important;
        }
    }

    .ag-font-size-16 {
        .ag-header-cell-text {
            font-size: 16px !important;
        }

        .ag-cell-value {
            font-size: 16px !important;
        }

        .ag-status-bar {
            font-size: 16px !important;
        }
    }

    .ag-theme-alpine-property-setup {
        // @extend .ag-theme-alpine-partner;
        .ag-root-wrapper {
            border-color: #fff !important;
            border-radius: 5px !important;
            padding: 5px !important;
        }
    }

    .ag-theme-alpine-owner-statement {
        @extend .ag-theme-alpine-report;

        .ag-root-wrapper {
            border: 2px solid !important;
            border-color: var(--ag-row-border-color, var(--ag-secondary-border-color, #dde2eb)) !important;
            border-radius: 5px !important;
            padding: 5px !important;
        }
    }

    .ag-theme-alpine-owner-statement-totals {
        @extend .ag-theme-alpine-owner-statement;

        .ag-header {
            display: none;
        }
    }
</style>

<style lang="scss" scoped>
    .lx-grid {
        position: relative;
    }

    .btn-toolbar {
        margin-left: 0px !important;
        padding-left: 0px;
    }

    .lx-grid-buttons {
        display: flex;
        flex-wrap: wrap-reverse !important;
        justify-content: space-between !important;
        margin-bottom: 5px;
    }

    @media print {
        .lx-grid-buttons {
            display: none !important;
        }
    }

    .lx-grid-buttons-toolbar > ::deep(*) {
        margin-bottom: 5px;
    }

    :deep(.ag-cell-label-container) {
        padding: 0;
        .ag-sort-indicator-container .ag-sort-indicator-icon.ag-sort-order {
            display: none;
        }
    }

    :deep(.ag-header-cell-text) {
        white-space: normal;
        word-break: auto-phrase;
    }

    :deep(.ag-group-child-count) {
        display: none !important;
    }

    :deep(.ag-cell .btn svg) {
        vertical-align: -0.125em;
    }

    :deep(.ag-cell > .ag-cell-wrapper) {
        position: relative;
        top: 50%;
        transform: translate(0, -50%);
    }

    :deep(.lx-editable-cell.ag-cell-value, .lx-editable-cell .ag-cell-value) {
        text-decoration: underline;
        cursor: pointer;
    }

    :deep(.lx-switch-cell) {
        text-overflow: unset;
    }

    :deep(.lx-row-group) {
        font-weight: bold;
        background-color: #f2fbfc;
    }

    :deep(.lx-owner-statement-row) {
        font-weight: 600;
        background-color: #fff;
    }

    :deep(.lx-additional-tables-row) {
        font-weight: 400;
        background-color: #fff;
    }

    :deep(.lx-cell-negative) {
        color: #ff0000;
    }

    :deep(.lx-cell-wrap) {
        white-space: normal;
        line-height: 1em;
        overflow-y: hidden;
    }

    :deep(.guest-status-cell) {
        .dashboard-guest-status-icon {
            width: 24px;
            height: 23px;
            margin-right: 0.5rem;
        }
    }

    :deep(.ag-status-name-value) {
        padding-bottom: 6px !important;
        padding-top: 6px !important;
    }
</style>
